import React, { useState, useEffect } from 'react';
import { reportApi, projectApi } from '../services/api';
import { Card, Select, Spinner } from '../components/UI';
import { UsersThree, HardHat, Package } from '@phosphor-icons/react';

export default function SiteDashboardPage() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { projectApi.list().then(r => setProjects(r.data)).finally(() => setLoading(false)); }, []);

  useEffect(() => {
    reportApi.siteDashboard(selectedProject || undefined).then(r => setData(r.data));
  }, [selectedProject]);

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5" data-testid="site-dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Site Dashboard</h1>
          <p className="text-sm text-[#666]">Team productivity, manpower & material usage</p>
        </div>
        <Select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="w-64" data-testid="select-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </div>

      {!data ? (
        <Card className="p-8 text-center text-[#666]">Loading...</Card>
      ) : (
        <>
          {/* Team Productivity */}
          {data.teams.length > 0 && (
            <Card className="p-4" data-testid="team-productivity">
              <h3 className="text-sm font-semibold text-[#0A0A0A] mb-3 flex items-center gap-2">
                <UsersThree className="w-4 h-4 text-[#002FA7]" /> Team Productivity
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                      <th className="pb-2">Team</th><th className="pb-2">Category</th>
                      <th className="pb-2 text-right">Logs</th><th className="pb-2 text-right">Workers</th><th className="pb-2 text-right">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.teams.map((t, i) => (
                      <tr key={i} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]">
                        <td className="py-2 font-medium">{t.name}</td>
                        <td className="py-2 capitalize text-[#666]">{t.category}</td>
                        <td className="py-2 text-right">{t.log_count}</td>
                        <td className="py-2 text-right">{t.total_workers}</td>
                        <td className="py-2 text-right font-bold">{t.total_hours}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Manpower by Category */}
          {data.manpower_by_category.length > 0 && (
            <Card className="p-4" data-testid="manpower-summary">
              <h3 className="text-sm font-semibold text-[#0A0A0A] mb-3 flex items-center gap-2">
                <HardHat className="w-4 h-4 text-[#FFAB00]" /> Manpower Summary
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {data.manpower_by_category.map((m, i) => (
                  <div key={i} className="bg-[#F9F9F9] rounded-lg p-3 text-center">
                    <p className="text-xs text-[#666] capitalize">{m.category}</p>
                    <p className="text-lg font-bold text-[#0A0A0A]">{m.workers} <span className="text-xs font-normal text-[#666]">workers</span></p>
                    <p className="text-sm text-[#002FA7] font-medium">{m.hours} hrs</p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Material Usage */}
          {data.material_usage.length > 0 && (
            <Card className="p-4" data-testid="material-summary">
              <h3 className="text-sm font-semibold text-[#0A0A0A] mb-3 flex items-center gap-2">
                <Package className="w-4 h-4 text-[#00C853]" /> Material Usage
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                      <th className="pb-2">Material</th><th className="pb-2 text-right">Received</th>
                      <th className="pb-2 text-right">Used</th><th className="pb-2 text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.material_usage.map((m, i) => (
                      <tr key={i} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]">
                        <td className="py-2 font-medium">{m.material}</td>
                        <td className="py-2 text-right">{m.received}</td>
                        <td className="py-2 text-right">{m.used}</td>
                        <td className={`py-2 text-right font-bold ${m.balance >= 0 ? 'text-[#00C853]' : 'text-[#FF3333]'}`}>{m.balance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
